import { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/**
 * Reverb-powered typeahead for AddPedalForm / AddWantForm.
 *
 * Two hooks, one shared engine — both call the single `searchReverbSuggestions`
 * callable (passing type: 'model' | 'brand'):
 *   - useReverbProductSuggestions({ query, make }) → model suggestions.
 *   - useReverbBrandSuggestions({ query })         → canonical brand suggestions.
 *
 * Both query the canonical Reverb catalog and are tuned for a snappy feel
 * despite Reverb's variable latency:
 *
 *   - 2-char minimum gate — below this we don't query at all.
 *   - 150ms debounce — fires one request after the user pauses, not per
 *     keystroke. Short, because this is interactive suggestion UI.
 *   - Module-level cache + in-flight dedupe — a key we've already resolved
 *     returns instantly with ZERO network (critical for backspace / retype).
 *     Concurrent requests for the same key share one promise. Keys are
 *     namespaced ('products' vs 'brands') so the two hooks never collide.
 *   - Word-level matching with typo tolerance — typed tokens match candidate
 *     words as an in-order subsequence, each token by prefix ("fiel" → "Field")
 *     or one edit for full words >= 5 chars ("marshall" → "Marshal"). Extra
 *     words in the canonical title, a missing brand ("big muff"), or a single
 *     typo no longer blank the list the way whole-string prefix matching did.
 *     Dashes fold to spaces and dash runs keep a joined alias, so "electro
 *     harmonix", "electro-harmonix" and "electroharmonix" all match
 *     "Electro-Harmonix" — see normalize() / candidateWords() below.
 *   - Ranking — items whose first word matches the first typed token group
 *     above mid-title mentions ("boss" ranks stock Boss above Keeley-mod
 *     titles), preserving Reverb's relevance order within each group.
 *   - Resolve behavior, gated by fetch generation — each query change bumps a
 *     monotonic id (fetchSeqRef). A request may touch state ONLY if it's still
 *     the newest generation; a stale response (a slow "bos" landing after "boss"
 *     fired) is dropped outright — no state write, no spinner clear. When the
 *     newest request resolves:
 *       • WITH results → full REPLACE (no cross-query merge). A query that yields
 *         matches owns the list.
 *       • EMPTY → RETAIN still-matching items; if nothing is retained and the
 *         query has multiple tokens, FALLBACK RE-QUERY: drop trailing tokens
 *         ("thorpyfx fiel" → "thorpyfx") until a fetch yields candidates, then
 *         filter those against the full typed query. This covers fast typing /
 *         paste / cold loads where no earlier keystroke fetched candidates to
 *         retain — the mid-word "No matches" dead end.
 *   - LOCAL-FIRST narrowing — on each keystroke the current list is filtered
 *     down to items that still match (synchronous, never blanks). If the local
 *     working set satisfies the new query, that IS the answer: no network call
 *     fires at all. Reverb is consulted only when the local filter yields zero
 *     (or an exact-query cache entry exists, which replaces for free — so
 *     backspacing restores the full earlier result set). Narrowing only
 *     REMOVES items; it never combines two different query strings' results.
 *   - loading flag — true ONLY while the most recent network request is in
 *     flight (false for cache hits / too-short queries). The forms use this to
 *     render a "Searching…" row and to avoid closing the dropdown mid-request.
 *
 * Failure posture: any error resolves to an empty list (and is NOT cached, so
 * the next keystroke can retry).
 */

const MIN_QUERY_LENGTH = 2;
// 120ms: short enough that suggestions feel live while typing, long enough to
// coalesce a fast burst of keystrokes into one Reverb call.
const DEBOUNCE_MS = 120;

// Cap on the retained working set so it can't grow unbounded as a user types
// through many prefixes. MUST match the backend's per-response pool (50) or we
// re-introduce the bug the backend widening fixed: this slice is applied after
// ranking, so a lower cap here silently discards the tail of what the server
// found — which is exactly where JHS NOTAKLÖN lived (index 24 of "jhs"). The
// FORM caps what it displays (~8 rows); this pool is only what local narrowing
// and the fallback re-query filter against.
const MAX_RETAINED = 50;

// Module-level cache shared across all hook instances / form fields.
// namespaced key -> { data: Array } (resolved) | { promise: Promise<Array> } (in flight)
const cache = new Map();

// Diacritics are folded away before matching. Without this the one-edit typo
// budget was silently doing double duty as accent tolerance, and only stretched
// far enough for a single accent: "notaklon" reached "NOTAKLÖN" (one
// substitution) but "notadumble" could never reach "NOTADÜMBLË" (two), so a
// pedal Reverb HAD returned was dropped by our own filter. Folding makes accents
// free and leaves the edit budget for real typos.
//
// Dashes are folded to spaces for the same reason. "Electro-Harmonix" tokenized
// as one word meant typing "electro harmonix big" blanked the list: "electro"
// prefix-matched the hyphenated word, but "harmonix" then had no standalone word
// left to match, and EVERY EHX title failed the same way. On mobile the hyphen
// is a symbols-keyboard trip users understandably skip \u2014 and iOS smart
// punctuation can silently turn typed hyphens into en/em dashes, hence the full
// \u2010-\u2015 range (+ \u2212 minus). This mirrors normalizePedalName in
// functions/matching.js, which has folded -/_ since the beginning \u2014 the
// typeahead was the one text-matching surface that never got the treatment.
const DASH_CHARS = /[-_\u2010-\u2015\u2212]/g;

// Accent fold + lowercase only \u2014 no dash folding. candidateWords() needs the
// dash positions intact to know which word runs were hyphen-connected.
function foldAccents(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function normalize(s) {
  return foldAccents(s)
    .replace(DASH_CHARS, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function cacheKey(namespace, make, query) {
  return `${namespace}:${normalize(make)}|${normalize(query)}`;
}

function tokenize(s) {
  return normalize(s).split(' ').filter(Boolean);
}

// A candidate's words for matching, plus a joined ALIAS for every dash-connected
// run: "Op-Amp" contributes the words ["op","amp"] AND the alias "opamp" at that
// position, so all three typed spellings \u2014 "op amp", "op-amp", "opamp" \u2014 reach
// it. Aliases come ONLY from dash boundaries, deliberately not from plain
// spaces: "bigmuff" never matched "Big Muff" before this change and still
// doesn't, which keeps this strictly a dash-tolerance fix rather than a general
// matching broadening. (Before dash folding, the joined spelling happened to
// work for single-hyphen words via the one-edit budget \u2014 "opamp" was one
// insertion from "op-amp" \u2014 so without the alias, folding would have silently
// REGRESSED that spelling while fixing the spaced one.)
function candidateWords(s) {
  const words = [];
  const aliasAt = new Map(); // word index -> { joined, length }
  for (const run of foldAccents(s).trim().split(/\s+/)) {
    const parts = run.split(DASH_CHARS).filter(Boolean);
    if (parts.length > 1) {
      aliasAt.set(words.length, { joined: parts.join(''), length: parts.length });
    }
    words.push(...parts);
  }
  return { words, aliasAt };
}

// One-edit tolerance (Damerau-Levenshtein <= 1): a single substitution,
// insertion, deletion, or adjacent transposition. Cheap single pass — no
// distance matrix — because we only ever need the <= 1 answer.
function withinOneEdit(a, b) {
  if (a === b) return true;
  const la = a.length;
  const lb = b.length;
  if (Math.abs(la - lb) > 1) return false;
  if (la === lb) {
    let i = 0;
    while (i < la && a[i] === b[i]) i += 1;
    if (i === la) return true;
    if (a.slice(i + 1) === b.slice(i + 1)) return true; // substitution
    return (
      a[i] === b[i + 1] && a[i + 1] === b[i] && a.slice(i + 2) === b.slice(i + 2)
    ); // adjacent transposition
  }
  const short = la < lb ? a : b;
  const long = la < lb ? b : a;
  let i = 0;
  while (i < short.length && short[i] === long[i]) i += 1;
  return short.slice(i) === long.slice(i + 1); // single insertion/deletion
}

// A typed token matches a candidate word if it's a prefix (mid-typing: "fiel"
// → "field") or a one-edit misspelling ("marshall" → "marshal", "fied" →
// "field"). Fuzzy kicks in at >= 4 chars — real typos land on short words too
// (observed: "fied" for "field"), and candidates are already scoped by the
// fetch, so the looser threshold can't flood the list. Prefix is checked
// first, and ranking keeps exact-prefix matches above fuzzy ones' titles.
function tokenMatchesWord(token, word) {
  if (word.startsWith(token)) return true;
  return token.length >= 4 && withinOneEdit(token, word);
}

// Word-level matching, replacing the old whole-string startsWith. Every typed
// token must match a candidate word as an IN-ORDER subsequence, so:
//   "thorpyfx fiel"  matches "ThorpyFX Field Marshal Fuzz"  (prefix per word)
//   "big muff"       matches "Electro-Harmonix Big Muff Pi" (no brand typed)
//   "thorpyfx marshal" matches too (skipped word is fine, order preserved)
// The old whole-string prefix silently failed all three shapes — any extra
// word in the canonical title, any missing brand, any typo blanked the list.
function matchesQuery(item, matchKey, normQuery) {
  const tokens = tokenize(normQuery);
  if (!tokens.length) return false;
  const { words, aliasAt } = candidateWords(item?.[matchKey]);
  let wi = 0;
  for (const token of tokens) {
    let found = -1;
    let consumed = 1;
    for (let i = wi; i < words.length; i += 1) {
      // The single word is tried FIRST, the dash-run alias only when it fails.
      // Alias-first would be wrong: "electro" prefix-matches the alias
      // "electroharmonix" too, and consuming both words would leave the next
      // typed token ("harmonix") facing "big" — blanking the exact spaced
      // spelling this fix exists for.
      if (tokenMatchesWord(token, words[i])) { found = i; consumed = 1; break; }
      const alias = aliasAt.get(i);
      if (alias && tokenMatchesWord(token, alias.joined)) {
        found = i;
        consumed = alias.length;
        break;
      }
    }
    if (found === -1) return false;
    // Advance past EVERYTHING the token consumed. Advancing only one position
    // after an alias match would let the next typed token re-match the tail of
    // the same dash run ("jb2 2" falsely matching "JB-2").
    wi = found + consumed;
  }
  return true;
}

// Stable rank: items whose FIRST word matches the FIRST typed token group
// first — typing "boss" puts stock Boss pedals above "Keeley (Boss) mod"
// titles that merely contain the word. Reverb's relevance order is preserved
// within each group (plain index tiebreak), so this never scrambles results.
function rankMatches(items, matchKey, normQuery) {
  const firstToken = tokenize(normQuery)[0];
  if (!firstToken) return items;
  return items
    .map((item, i) => {
      const { words, aliasAt } = candidateWords(item?.[matchKey]);
      // A leading dash run counts by its joined alias too, so the joined
      // spelling ("electroharmonix…") ranks EHX titles first the same way the
      // spaced and hyphenated spellings do.
      const alias0 = aliasAt.get(0);
      const group =
        (words[0] || '').startsWith(firstToken) ||
        (alias0 && alias0.joined.startsWith(firstToken))
          ? 0
          : 1;
      return { item, i, group };
    })
    .sort((a, b) => a.group - b.group || a.i - b.i)
    .map((x) => x.item);
}

async function fetchFromCallable(callableName, args, key) {
  const cached = cache.get(key);
  if (cached?.data !== undefined) return cached.data;
  if (cached?.promise) return cached.promise;

  const promise = (async () => {
    try {
      const fn = httpsCallable(functions, callableName);
      const result = await fn(args);
      const data = Array.isArray(result?.data?.results) ? result.data.results : [];
      cache.set(key, { data });
      return data;
    } catch {
      // Don't persist failures — drop the entry so the next keystroke retries
      // (e.g. transient Reverb outage / rate limit shouldn't poison the cache).
      cache.delete(key);
      return [];
    }
  })();

  cache.set(key, { promise });
  return promise;
}

// Fire-and-forget prewarm. Call this the instant a user lands on a surface that
// will use the typeahead (Add Pedal / Add Want) so the callable's container is
// spinning up BEFORE they start typing — turning Reverb's ~2s cold start into
// latency they never see, without paying to keep an instance warm.
//
// The trick: an empty query short-circuits server-side (query.length <
// MIN_QUERY_LENGTH) and returns immediately AFTER auth — before any Reverb call
// or Firestore write. So this pays only the cold-start spin-up, burns zero
// Reverb quota, and writes no cache. Deduped per page load; a failure resets the
// flag so a later mount can retry.
let prewarmInFlight = false;
export function prewarmReverbSuggestions() {
  if (prewarmInFlight) return;
  prewarmInFlight = true;
  try {
    const fn = httpsCallable(functions, 'searchReverbSuggestions');
    fn({ type: 'model', query: '' }).catch(() => { prewarmInFlight = false; });
  } catch {
    prewarmInFlight = false;
  }
}

/**
 * Shared engine behind both public hooks.
 * @returns {{ suggestions: Array, loading: boolean }}
 */
function useReverbSuggestions({ namespace, callableName, type, query, make, includeMake, matchKey }) {
  const trimmedQuery = (query || '').trim();
  const trimmedMake = (make || '').trim();
  const normQuery = normalize(trimmedQuery);
  const tooShort = trimmedQuery.length < MIN_QUERY_LENGTH;
  const key = cacheKey(namespace, includeMake ? trimmedMake : '', trimmedQuery);

  const [suggestions, setSuggestions] = useState(() => {
    const cached = cache.get(key);
    return cached?.data
      ? rankMatches(cached.data.filter((i) => matchesQuery(i, matchKey, normQuery)), matchKey, normQuery)
      : [];
  });
  const [loading, setLoading] = useState(false);

  // Mirror of `suggestions` readable synchronously inside the effect + async
  // resolvers (state itself is a render-cycle behind). Written ONLY via
  // applySuggestions so the two can't drift.
  const suggestionsRef = useRef(suggestions);

  // Track the brand context so a model search can drop retained items from a
  // previous brand. Monotonic id so only the newest fetch owns state (results +
  // loading) — see the stale guard in the effect below.
  const makeRef = useRef(trimmedMake);
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    // Every query change opens a new fetch "generation". The debounced request
    // below captures this id; when it resolves, it only touches state if it's
    // STILL the newest generation. Bumping here (before the cache/short-circuit
    // paths too) guarantees an older in-flight request — e.g. a slow "bos" — is
    // neutralized the instant "boss" starts, so it can never clear the spinner
    // or write its results over the newer query's.
    const fetchId = ++fetchSeqRef.current;

    const applySuggestions = (arr) => {
      suggestionsRef.current = arr;
      setSuggestions(arr);
    };

    // Below the min length → cleared/new search; reset the working set.
    if (tooShort) {
      applySuggestions([]);
      setLoading(false);
      return undefined;
    }

    // New brand context (model search re-scoped) → discard the old brand's items.
    if (includeMake && makeRef.current !== trimmedMake) {
      makeRef.current = trimmedMake;
      applySuggestions([]);
    }

    // FALLBACK RE-QUERY — the safety net for "Reverb found nothing AND we have
    // nothing retained". Reverb's CSP search is word-based, so a query ending
    // in a partial word ("thorpyfx fiel") legitimately returns zero; retention
    // papers over that ONLY if an earlier keystroke already fetched the right
    // candidates. Fast typing / paste / a cold page skips those intermediate
    // fetches and used to dead-end at "No matches" mid-word. Here we retry with
    // trailing tokens dropped ("thorpyfx fiel" → "thorpyfx") until some fetch
    // yields candidates, then filter those against the FULL typed query with
    // token+fuzzy matching. Bounded by token count; every attempt goes through
    // the shared cache; stale-guarded at each await.
    const fallbackFetch = async () => {
      const tokens = tokenize(trimmedQuery);
      for (let k = tokens.length - 1; k >= 1; k -= 1) {
        const partial = tokens.slice(0, k).join(' ');
        if (partial.length < MIN_QUERY_LENGTH) break;
        const partialKey = cacheKey(namespace, includeMake ? trimmedMake : '', partial);
        const args = includeMake
          ? { type, query: partial, make: trimmedMake }
          : { type, query: partial };
        const data = await fetchFromCallable(callableName, args, partialKey);
        if (fetchId !== fetchSeqRef.current) return; // newer query took over
        const matched = data.filter((item) => matchesQuery(item, matchKey, normQuery));
        if (matched.length > 0) {
          applySuggestions(rankMatches(matched, matchKey, normQuery).slice(0, MAX_RETAINED));
          setLoading(false);
          return;
        }
      }
      if (fetchId !== fetchSeqRef.current) return;
      setLoading(false); // genuinely nothing — let the no-match UI settle
    };

    // Shared resolve-empty path (network AND cache): keep whatever retained
    // items still match; if that leaves nothing and the query has multiple
    // tokens, escalate to the fallback re-query instead of blanking.
    const resolveEmpty = () => {
      const retained = suggestionsRef.current.filter((item) => matchesQuery(item, matchKey, normQuery));
      applySuggestions(rankMatches(retained, matchKey, normQuery));
      if (retained.length === 0 && tokenize(trimmedQuery).length > 1) {
        setLoading(true);
        fallbackFetch();
        return true; // async continuation owns the spinner now
      }
      return false;
    };

    // Instant narrow: keep only currently-shown items that still match the new
    // query. Synchronous, zero network — the list can never blank on a
    // keystroke. This ONLY removes items; it never merges in another query's
    // results.
    const narrowed = rankMatches(
      suggestionsRef.current.filter((item) => matchesQuery(item, matchKey, normQuery)),
      matchKey,
      normQuery
    );
    applySuggestions(narrowed);

    // Resolved cache entry for this EXACT query → replace from cache (no
    // spinner, no network). Checked before the local short-circuit below so
    // backspacing from "thorpyfx fiel" to "thorpyfx" restores the FULL cached
    // result set for "thorpyfx", not just the narrowed remnant.
    const cached = cache.get(key);
    if (cached?.data !== undefined) {
      const cachedFresh = cached.data.filter((item) => matchesQuery(item, matchKey, normQuery));
      if (cachedFresh.length > 0) {
        applySuggestions(rankMatches(cachedFresh, matchKey, normQuery).slice(0, MAX_RETAINED));
        setLoading(false);
      } else if (narrowed.length > 0) {
        // Cached-empty for this exact string (Reverb blanks on partial words)
        // but the local candidates satisfy it — keep them, done.
        setLoading(false);
      } else if (!resolveEmpty()) {
        setLoading(false);
      }
      return undefined;
    }

    // The narrowed local set is shown instantly (above) so the list never blanks
    // on a keystroke, but it is NOT the final answer. Until 2026-10-18 this was a
    // local-first short-circuit: if anything local still matched, Reverb was not
    // asked again. That hid real pedals: Reverb's relevance pool for "boss b" is
    // bass pedals and modded BD-2s (Keeley, Analogman) and does not contain the
    // stock "Boss BD-2 Blues Driver" at all, so narrowing to "boss bd" showed
    // only the mods, while the pool for "boss bd" itself has the stock BD-2
    // first. The exact query is now always fetched (debounced; the server caches
    // it for 7 days so the cost is one round trip per distinct string) and, when
    // it returns matches, REPLACES the narrowed remnant. The spinner only shows
    // when there is nothing local to display meanwhile.
    setLoading(narrowed.length === 0);
    const timer = setTimeout(() => {
      // The FOLDED query goes upstream, not the raw text. The cache key is
      // already folded, so sending raw text would let two spellings ("op-amp" /
      // "op amp") race to populate one key with results from different Reverb
      // requests — whichever spelling fetched first would own the doc. Deriving
      // key and request from the same string removes that order-dependence, and
      // sidesteps ever needing to know how Reverb tokenizes an em dash.
      const args = includeMake
        ? { type, query: normQuery, make: trimmedMake }
        : { type, query: normQuery };
      fetchFromCallable(callableName, args, key).then((data) => {
        // STALE GUARD: only the newest generation may touch state. A late/stale
        // response (a "bos" landing after "boss" fired) returns here — it neither
        // replaces the list nor clears the spinner.
        if (fetchId !== fetchSeqRef.current) return;
        const fresh = data.filter((item) => matchesQuery(item, matchKey, normQuery));
        if (fresh.length > 0) {
          // Reverb returned matches for THIS query → full replace, no cross-query
          // merge (ranked so first-word matches beat mid-title mentions).
          applySuggestions(rankMatches(fresh, matchKey, normQuery).slice(0, MAX_RETAINED));
          setLoading(false);
        } else if (!resolveEmpty()) {
          setLoading(false);
        }
      });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [trimmedQuery, trimmedMake, normQuery, tooShort, key, namespace, callableName, type, includeMake, matchKey]);

  return { suggestions, loading };
}

/**
 * Reverb model typeahead.
 * @param {{ query?: string, make?: string }} args
 *   query — the text in the model field.
 *   make  — optional brand context; scopes the Reverb query to that brand.
 * @returns {{ suggestions: Array, loading: boolean }}
 *   suggestions — [{ make, model, title, usedTotal, slug }]
 */
export function useReverbProductSuggestions({ query, make } = {}) {
  return useReverbSuggestions({
    namespace: 'products',
    callableName: 'searchReverbSuggestions',
    type: 'model',
    query,
    make,
    includeMake: true,
    matchKey: 'model',
  });
}

/**
 * Reverb combined make+model typeahead — powers the single "Search for your
 * pedal" field on the Add Pedal wizard's first step. Unlike
 * useReverbProductSuggestions (which scopes to a brand and prefix-matches the
 * MODEL), this searches the whole catalog with NO brand filter and prefix-
 * matches the full canonical `title` ("ThorpyFX Field Marshal Fuzz"), so a user
 * types brand + model in one box and picks the canonical Reverb entry. Selecting
 * a row yields { make, model, title, slug } — the exact make/model the Reverb
 * price lookup expects, keeping the price check consistent.
 * @param {{ query?: string }} args
 * @returns {{ suggestions: Array, loading: boolean }}
 *   suggestions — [{ make, model, title, usedTotal, slug }]
 */
export function useReverbCombinedSuggestions({ query } = {}) {
  return useReverbSuggestions({
    namespace: 'products',
    callableName: 'searchReverbSuggestions',
    type: 'model',
    query,
    make: '',
    includeMake: false,
    matchKey: 'title',
  });
}

/**
 * Reverb canonical-brand typeahead.
 * @param {{ query?: string }} args
 *   query — the text in the brand field.
 * @returns {{ suggestions: Array, loading: boolean }}
 *   suggestions — [{ make, slug }]
 */
export function useReverbBrandSuggestions({ query } = {}) {
  return useReverbSuggestions({
    namespace: 'brands',
    callableName: 'searchReverbSuggestions',
    type: 'brand',
    query,
    make: '',
    includeMake: false,
    matchKey: 'make',
  });
}
